import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Quote, Pencil, RotateCcw } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { ScoreBar } from "@/components/RatingStars";

const keyFor = (slug) => `hijrah-mom-opinion-${slug}`;

const loadOverride = (slug) => {
  try {
    return JSON.parse(localStorage.getItem(keyFor(slug)) || "null");
  } catch {
    return null;
  }
};

const COMING_SOON = ["uae", "saudi", "qatar", "indonesia", "egypt", "morocco"];

export const MomTakeSection = ({ country }) => {

  const [override, setOverride] = useState(() => loadOverride(country.slug));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  const mom = override || country.mom;

  const openEditor = () => {
    setDraft({
      verdict: mom.verdict,
      pros: mom.pros.join("\n"),
      cons: mom.cons.join("\n"),
    });
    setEditing(true);
  };

  const save = () => {
    const cleaned = {
      verdict: draft.verdict.trim(),
      pros: draft.pros.split("\n").map((s) => s.trim()).filter(Boolean),
      cons: draft.cons.split("\n").map((s) => s.trim()).filter(Boolean),
      scores: country.mom.scores,
    };
    localStorage.setItem(keyFor(country.slug), JSON.stringify(cleaned));
    setOverride(cleaned);
    setEditing(false);
  };

  const reset = () => {
    localStorage.removeItem(keyFor(country.slug));
    setOverride(null);
    setEditing(false);
  };

  if (COMING_SOON.includes(country.slug)) {
    return (
      <section id="mom-take" className="bg-pine-ink grain relative text-cream py-16 lg:py-20" data-testid="mom-take-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 text-center">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-petal-soft">Mom's unfiltered take</p>
          <h2 className="mt-4 font-serif font-semibold text-3xl lg:text-4xl" data-testid="mom-take-coming-soon">Coming soon, insha'Allah.</h2>
          <p className="mt-3 text-sage-light max-w-xl mx-auto text-sm leading-relaxed">I share my take only on places I've walked myself. {country.name} is on the list — check back, or leave your own review in the Mom/Sister Reviews tab above.</p>
        </div>
      </section>
    );
  }

  return (
    <section id="mom-take" className="bg-pine-ink grain relative text-cream py-16 lg:py-24" data-testid="mom-take-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-4">
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-petal-soft">Mom's unfiltered take</p>
              {override && (
                <span data-testid="mom-edited-badge" className="font-mono text-[10px] tracking-widest uppercase bg-petal/20 text-petal-soft border border-petal/40 rounded-full px-3 py-1">
                  Your words
                </span>
              )}
              <button
                data-testid="mom-edit-btn"
                onClick={openEditor}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-sage-light hover:text-petal-soft border border-cream/15 hover:border-petal/50 rounded-full px-3.5 py-1.5 transition-colors"
              >
                <Pencil size={12} /> Make it mine
              </button>
            </div>
            <h2 className="mt-4 font-serif font-semibold text-3xl lg:text-5xl leading-tight">
              Would I move my kids to {country.name}?
            </h2>
            <div className="mt-8 relative">
              <Quote size={28} className="text-petal absolute -top-2 -left-1" />
              <p className="pl-10 font-serif italic text-xl lg:text-2xl leading-relaxed text-cream/90" data-testid="mom-verdict-text">
                {mom.verdict}
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-pine-card rounded-2xl p-6 border border-cream/10">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal-soft mb-4">What I love</p>
                <ul className="space-y-2.5">
                  {mom.pros.map((p) => (
                    <li key={p} className="flex gap-2.5 text-sm text-cream/85"><Check size={15} className="text-petal-soft shrink-0 mt-0.5" />{p}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-pine-card rounded-2xl p-6 border border-cream/10">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal-soft mb-4">What I warn you about</p>
                <ul className="space-y-2.5">
                  {mom.cons.map((c) => (
                    <li key={c} className="flex gap-2.5 text-sm text-cream/85"><X size={15} className="text-petal shrink-0 mt-0.5" />{c}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <FadeUp className="bg-pine-card rounded-3xl border border-cream/10 p-7 lg:sticky lg:top-28">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal-soft mb-6">Mom's scorecard</p>
              <div className="space-y-5">
                <ScoreBar label="Safety for women & children" value={country.mom.scores.safety} />
                <ScoreBar label="Muslim community" value={country.mom.scores.community} />
                <ScoreBar label="Affordability" value={country.mom.scores.cost} />
                <ScoreBar label="Easy bureaucracy" value={country.mom.scores.bureaucracy} />
              </div>
              <p className="mt-8 text-xs text-sage-light leading-relaxed border-t border-cream/10 pt-5">
                Scores are one mother's honest judgment from research and community experience — your family's fit may differ. Make istikhara, then verify.
              </p>
            </FadeUp>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {editing && draft && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-pine-ink/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setEditing(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              data-testid="mom-edit-modal"
              className="bg-cream text-ink rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-7 lg:p-9"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-serif font-semibold text-2xl lg:text-3xl text-pine">Your take on {country.name}</h3>
              <p className="mt-2 text-sm text-sage leading-relaxed">
                This is your site — say it in your words. Saved only in this browser; visitors see what you write here.
              </p>
              <div className="mt-6 space-y-5">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal block mb-2" htmlFor="mom-verdict">Your honest verdict</label>
                  <textarea
                    id="mom-verdict"
                    data-testid="mom-verdict-input"
                    rows={5}
                    value={draft.verdict}
                    onChange={(e) => setDraft({ ...draft, verdict: e.target.value })}
                    className="w-full bg-white border border-pine/15 rounded-xl px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-petal/40"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal block mb-2" htmlFor="mom-pros">What you love (one per line)</label>
                    <textarea
                      id="mom-pros"
                      data-testid="mom-pros-input"
                      rows={5}
                      value={draft.pros}
                      onChange={(e) => setDraft({ ...draft, pros: e.target.value })}
                      className="w-full bg-white border border-pine/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-petal/40"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal block mb-2" htmlFor="mom-cons">Your warnings (one per line)</label>
                    <textarea
                      id="mom-cons"
                      data-testid="mom-cons-input"
                      rows={5}
                      value={draft.cons}
                      onChange={(e) => setDraft({ ...draft, cons: e.target.value })}
                      className="w-full bg-white border border-pine/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-petal/40"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  data-testid="mom-save-btn"
                  onClick={save}
                  className="inline-flex items-center gap-2 bg-petal text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-pine transition-colors"
                >
                  <Check size={15} /> Save my take
                </button>
                {override && (
                  <button
                    data-testid="mom-reset-btn"
                    onClick={reset}
                    className="inline-flex items-center gap-2 border border-pine/25 text-pine text-sm font-semibold px-6 py-3 rounded-full hover:bg-blush-deep transition-colors"
                  >
                    <RotateCcw size={14} /> Restore original
                  </button>
                )}
                <button
                  data-testid="mom-cancel-btn"
                  onClick={() => setEditing(false)}
                  className="text-sm font-semibold text-sage hover:text-petal px-4 py-3 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
